import React, { useEffect, useRef, useState } from 'react'
import { Helmet } from 'react-helmet'
import { useSelector } from 'react-redux'
import {
  selectAllUsersFromState,
  selectCreditCardsFullPaginatedData,
  selectFilteredCreditCards,
  selectUsersFullPaginatedData,
} from 'src/app/AccountsBudgets/redux/accountsBudgets.selectors'
import BackgroundPageContent from 'src/components/BackgroundPageContent/BackgroundPageContent'
import BudgetsBox from 'src/components/BudgetsBox/BudgetsBox'
import BudgetsBoxWithFilters, {
  BudgetsFiltersBody,
} from 'src/components/BudgetsBoxWithFilters/BudgetsBoxWithFilters'
import LoadingBackgroundPage from 'src/components/LoadingBackgroundPage/LoadingBackgroundPage'
import ObjectBoxTitle from 'src/components/ObjectBoxTitle/ObjectBoxTitle'
import { UserDropdownInfiniteScrollProps } from 'src/components/UserDropdown/UserDropdown'
import { useCurrentUser } from 'src/context/UserProvider/useUser'
import { useBackgroundDispatch, useQueryParamArrayHandler } from 'src/hooks'
import { useTranslate } from 'src/localization/Trns'
import { CreditCardType } from 'src/models/creditCard.models'
import { accountsBudgetsRouteProps } from 'src/navigation/routeProps'
import { useQueryStatistics } from 'src/queries/statistics/statistics.queries'
import { StatisticsKeys } from 'src/queries/statistics/statistics.queries.constants'
import { getCreditCards } from 'src/redux/actions/data/creditCards.actions'
import { getUsers } from 'src/redux/actions/data/users.actions'
import {
  selectLocationFromState,
  selectQueryParams,
} from 'src/redux/selectors/router.selectors'
import { selectAppName } from 'src/redux/selectors/session.selectors'
import { css } from 'styled-components'

const AccountsBudgets: React.FC = () => {
  const translate = useTranslate()

  // CreditCardsDelegateCount is read/gated here but was historically fetched
  // by the parent Accounts.tsx dispatch; the page now subscribes to it
  // directly (React Query dedupes with the parent's query).
  const { results: statisticsResults } = useQueryStatistics({
    statisticsKeys: [
      StatisticsKeys.CreditCardsActiveSourceBudgetsEnabledCanIssueBudgetsCount,
      StatisticsKeys.CreditCardsActiveDelegateCount,
      StatisticsKeys.CreditCardsDelegateCount,
    ] as const,
  })
  const creditCardsActiveSourceBudgetsEnabledCanIssueBudgetsCount =
    statisticsResults[
      StatisticsKeys.CreditCardsActiveSourceBudgetsEnabledCanIssueBudgetsCount
    ].data ?? 0
  const creditCardsDelegateCount =
    statisticsResults[StatisticsKeys.CreditCardsDelegateCount].data ?? 0
  const creditCardsActiveDelegateCount =
    statisticsResults[StatisticsKeys.CreditCardsActiveDelegateCount].data ?? 0
  const creditCardsFullPaginatedData = useSelector(
    selectCreditCardsFullPaginatedData,
  )
  const filteredCreditCards = useSelector(selectFilteredCreditCards)
  const appName = useSelector(selectAppName)
  const user = useCurrentUser()
  const usersFullPaginatedData = useSelector(selectUsersFullPaginatedData)
  const recipientOptions = useSelector(selectAllUsersFromState)
  const queryParams = useSelector(selectQueryParams)
  const location = useSelector(selectLocationFromState)

  const dispatch = useBackgroundDispatch()

  const fetch = (filtersBody?: BudgetsFiltersBody, page?: number): void => {
    dispatch(
      getCreditCards({
        count: 10,
        page,
        type: CreditCardType.Delegate,
        sortField: 'activeUpdatedAt',
      }),
    )
  }

  useEffect((): void => {
    if (location.pathname === accountsBudgetsRouteProps.path) {
      fetch()
    }
  }, [location])

  const fetchBudgets = (
    filtersBody?: BudgetsFiltersBody,
    page?: number,
  ): void => {
    dispatch(
      getCreditCards({
        count: 10,
        page,
        type: CreditCardType.Delegate,
        sortField: 'activeUpdatedAt',
        ...filtersBody,
      }),
    )
  }

  const infiniteScrollProps = {
    ...creditCardsFullPaginatedData,
    fetch: fetchBudgets,
  }

  const fetchUsers = (filterInput?: string, page?: number): void => {
    dispatch(
      getUsers({
        q: filterInput,
        count: 10,
        page: page,
      }),
    )
  }

  const userInfiniteScrollProps: UserDropdownInfiniteScrollProps = {
    fetch: fetchUsers,
    ...usersFullPaginatedData,
  }

  const [clientHeight, setClientHeight] = useState<number | null>(null)
  const ref: React.MutableRefObject<HTMLDivElement | null> = useRef(null)

  const [viewportHeight, setViewportHeight] = useState({
    height: window.innerHeight,
  })

  const updateWindowSize = (): void => {
    setViewportHeight({ height: window.innerHeight })
  }

  useEffect(() => {
    setClientHeight(viewportHeight.height - 122)
  }, [ref?.current])

  useEffect(() => {
    window.addEventListener('resize', updateWindowSize)
    setClientHeight(viewportHeight.height - 122)
    return (): void => {
      window.removeEventListener('resize', updateWindowSize)
    }
  }, [viewportHeight, ref?.current])

  return !(
    statisticsResults[
      StatisticsKeys.CreditCardsActiveSourceBudgetsEnabledCanIssueBudgetsCount
    ].isSuccess &&
    statisticsResults[StatisticsKeys.CreditCardsActiveDelegateCount]
      .isSuccess &&
    statisticsResults[StatisticsKeys.CreditCardsDelegateCount].isSuccess
  ) ? (
    <LoadingBackgroundPage />
  ) : (
    <>
      <Helmet>
        <title>{`Budgets | ${appName}`}</title>
        <meta name="description" content={`Budgets page of ${appName}`} />
      </Helmet>
      <div
        css={css`
          height: 100%;
        `}
        ref={ref}
        id={'ref'}
      >
        <BackgroundPageContent>
          {user?.hasOrgBookkeeperPermissions ? (
            <BudgetsBoxWithFilters
              budgets={filteredCreditCards}
              isSlim={false}
              clientHeight={clientHeight || 0}
              totalBudgets={creditCardsDelegateCount}
              defaultTitle={
                <ObjectBoxTitle
                  title={translate('Budgets')}
                  static={{
                    label: translate('Active'),
                    count: creditCardsActiveDelegateCount,
                  }}
                />
              }
              infiniteScrollProps={infiniteScrollProps}
              readOnly={
                creditCardsActiveSourceBudgetsEnabledCanIssueBudgetsCount === 0
              }
              userInfiniteScrollProps={userInfiniteScrollProps}
              recipientOptions={recipientOptions}
              initialFilterValues={{
                users: useQueryParamArrayHandler(queryParams.users),
                owners: useQueryParamArrayHandler(queryParams.owners),
                statuses: useQueryParamArrayHandler(queryParams.statuses),
              }}
            />
          ) : (
            <BudgetsBox
              budgets={filteredCreditCards}
              isSlim={false}
              clientHeight={clientHeight || 0}
              defaultTitle={
                <ObjectBoxTitle
                  title={translate('Budgets')}
                  static={{
                    label: translate('Active'),
                    count: creditCardsActiveDelegateCount,
                  }}
                />
              }
              infiniteScrollProps={{
                ...creditCardsFullPaginatedData,
                fetch,
              }}
              readOnly={
                creditCardsActiveSourceBudgetsEnabledCanIssueBudgetsCount === 0
              }
            />
          )}
        </BackgroundPageContent>
      </div>
    </>
  )
}

export default AccountsBudgets
